import Parameter from "./parameter";
import ModuleCard from "./module-card";
import ListView from "./list-view";
import Field from "./field";
import Entity from "./entity";
import Api from "./api";
import { arrayRemove, clone, uniq } from "../utils";
import IModuleCard from "../interface/module-card";
import IListView from "../interface/list-view";
import IList from "../interface/list";
import IField from "../interface/field";
import IEntity from "../interface/entity";
import ICardWidget from "../interface/card-widget";
import ICardList from "../interface/card-list";
import ICardGraphic from "../interface/card-graphic";
import IApiField from "../interface/api/api-field";
import ParamStorage from "../enum/param-storage";
import ErrorCode from "../enum/error-code";
import CardType from "../enum/card-type";
import ParameterStorage from "../class/parameter-storage";

class ListInstance {

    /* Public Methods */

    public getAll(): IList[] {
        return this.getStore().getAll().filter((list: IList) => this.valid(list));
    }

    public get(id: number): IList | null {
        const list: IList | null = this.getStore().get(id);
        if (list && this.valid(list)) {
            return list;
        }
        return null;
    }

    public async rename(id: number, label: string): Promise<IModuleCard | null> {
        const card: IModuleCard | null = ModuleCard.getAll().find((c: IModuleCard) => {
            if (c.type === CardType.List || c.type === CardType.Widget || c.type === CardType.Graphic) {
                return (c as ICardList | ICardWidget | ICardGraphic).listId === id;
            }
            return false;
        }) || null;
        if (card) {
            return ModuleCard.rename(card.id as number, label);
        }
        return null;
    }

    public async getByRelation(pointerId: number): Promise<IList | null> {
        const field: IApiField | null = Api.getField(pointerId);
        if (field && field.pointer) {
            const list: IList | null = this.getListByPointerId(pointerId);
            if (list) {
                return list;
            } else {
                return await this.create(field.pointer[0].ptEcn, field.singular, true, field.id);
            }
        } else {
            const list: IList | null = this.getListByPointerId(pointerId);
            if (list) {
                await this.delete(list.id as number);
            }
            return null;
        }
    }

    public async create(entityId: number, singular: string, editable: boolean = true, relationId: number | null = null): Promise<IList> {
        let entity: IEntity | null;
        if (entityId > 0) {
            entity = Entity.get(entityId);
        } else {
            entity = await Entity.create(singular);
        }
        if (entity) {
            try {
                return await this.getStore().create({
                    defaultViewId: null,
                    entityId: entity.id,
                    useGlobal: true,
                    deletable: true,
                    relationId,
                    views: [],
                    editable
                });
            } catch (e) {
                throw ErrorCode.ApiInternal;
            }
        } else {
            throw ErrorCode.NoEntity;
        }
    }

    public async delete(id: number): Promise<number> {
        const list: IList | null = this.getStore().get(id);
        if (list) {
            for (let i = 0; i < list.views.length; i++) {
                await ListView.delete(list.views[i]);
            }
            return await this.getStore().delete(id);
        }
        return id;
    }

    public async toggleUseGlobal(listId: number, value: boolean): Promise<IListView | number> {
        const list: IList | null = this.get(listId);
        if (list) {
            list.useGlobal = value;
            await this.update(list);
            const view: IListView | null = await ListView.getEntityGlobal(list.entityId);
            if (view) {
                return value ? view : view.id as number;
            } else {
                throw ErrorCode.BadArguments;
            }
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public async setViewsOrder(listId: number, order: number[]): Promise<IList> {
        const list: IList | null = this.get(listId);
        if (list) {
            list.views = list.views.sort((a: number, b: number) => {
                return order.indexOf(a) - order.indexOf(b);
            });
            return await this.update(list);
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    /* Private Public Methods */

    public async update(list: IList): Promise<IList> {
        list.views = uniq(list.views);
        list.views = list.views.filter((id: number) => !!ListView.get(id));
        if (!list.views.length) {
            list.useGlobal = true;
        }
        return await this.getStore().update(list);
    }

    public async clone(listId: number): Promise<IList | null> {
        const list: IList | null = this.get(listId);
        if (list) {
            const views: number[] = [];
            let defaultViewId: number | null = null;
            for(let i = 0; i < list.views.length; i++) {
                const view: IListView | null = await ListView.clone(list.views[i], 0, false);
                if (view) {
                    if (list.defaultViewId === list.views[i]) {
                        defaultViewId = view.id as number;
                    }
                    views.push(view.id as number);
                }
            }
            const cloneList: IList = clone(list);
            delete cloneList.id;
            cloneList.views = views;
            cloneList.defaultViewId = defaultViewId;
            return await this.getStore().create(cloneList);
        }
        return null;
    }

    public async addView(listId: number, viewId: number, isDefault: boolean = false): Promise<IList> {
        const list: IList | null = this.get(listId);
        if (list) {
            list.views.push(viewId);
            if (isDefault) {
                list.defaultViewId = viewId;
            }
            return await this.update(list);
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public async excludeView(viewId: number): Promise<void> {
        const lists: IList[] = this.getAll();
        for (let i = 0; i < lists.length; i++) {
            if (lists[i].views.includes(viewId)) {
                if (lists[i].defaultViewId === viewId) {
                    lists[i].defaultViewId = null;
                }
                arrayRemove(lists[i].views, viewId);
                await this.update(lists[i]);
            }
        }
    }

    public async setDefaultView(listId: number, viewId: number, value: boolean): Promise<IList> {
        const list: IList | null = this.get(listId);
        if (list) {
            if (value) {
                list.defaultViewId = viewId;
            } else if (list.defaultViewId === viewId) {
                list.defaultViewId = null;
            }
            return await this.update(list);
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public async deleteByEntityId(entityId: number): Promise<number[]> {
        const lists: IList[] = this.getAll().filter((l: IList) => l.entityId === entityId);
        const ids: number[] = lists.map((l: IList) => l.id as number);
        await ModuleCard.deleteByListsIds(ids);
        await this.getStore().bulk([], [], ids);
        return ids;
    }

    /* Private Methods */

    private valid(list: IList): boolean {
        const entity: IEntity | null = Entity.get(list.entityId);
        if (entity) {
            if (list.relationId) {
                const pointer: IField | null = Field.get(list.relationId);
                return pointer !== null;
            }
            return true;
        }
        return false;
    }

    private getListByPointerId(pointerId: number): IList | null {
        return this.getAll().find((list: IList) => list.relationId === pointerId) || null;
    }

    private getStore(): ParameterStorage<IList> {
        return Parameter.get(ParamStorage.Lists);
    }

}

const List = new ListInstance();

export default List;